import { useCallback, useEffect, useRef } from 'react';
import { shouldApplyHostOverrides } from './missionOverrideSync';

/**
 * Fast tick path — updates mission overrides only, no block-tree rewrite.
 * Host sync is deferred past the next paint so ticking a checkbox does not
 * re-render the whole mission view before the box can paint.
 */
export function useLibraryDocTicks({
  onTicksChange,
  masterTicksRef,
  lastReportedTicksRef,
  localTickOverrides,
  setLocalTickOverrides,
  tickOverrides,
  docId,
  missionId,
}) {
  const onTicksChangeRef = useRef(onTicksChange);
  onTicksChangeRef.current = onTicksChange;

  const notifyTimerRef = useRef(null);
  const localTickOverridesRef = useRef(localTickOverrides);
  localTickOverridesRef.current = localTickOverrides;

  const applyLocalTickOverrides = useCallback((next) => {
    localTickOverridesRef.current = next;
    lastReportedTicksRef.current = JSON.stringify(next);
    setLocalTickOverrides(next);
  }, [lastReportedTicksRef, setLocalTickOverrides]);

  const notifyHost = useCallback((overrides) => {
    clearTimeout(notifyTimerRef.current);
    notifyTimerRef.current = setTimeout(() => {
      notifyTimerRef.current = null;
      onTicksChangeRef.current?.(overrides);
    }, 150);
  }, []);

  const identityRef = useRef({ docId, missionId });
  const pendingEchoRef = useRef(false);

  useEffect(() => {
    const incoming = tickOverrides && typeof tickOverrides === 'object' ? tickOverrides : {};
    const serialized = JSON.stringify(incoming);
    const identityChanged =
      identityRef.current.docId !== docId ||
      identityRef.current.missionId !== missionId;
    identityRef.current = { docId, missionId };

    const decision = shouldApplyHostOverrides({
      identityChanged,
      incomingEqualsLastPushed: serialized === lastReportedTicksRef.current,
      incomingEqualsLocal: serialized === JSON.stringify(localTickOverridesRef.current || {}),
      pendingEcho: pendingEchoRef.current,
    });
    if (decision.clearPending) pendingEchoRef.current = false;
    if (!decision.apply) return;
    applyLocalTickOverrides(incoming);
  }, [applyLocalTickOverrides, docId, lastReportedTicksRef, missionId, tickOverrides]);

  useEffect(() => () => {
    // Flush a pending host sync on unmount so the last tick isn't lost
    if (notifyTimerRef.current) {
      clearTimeout(notifyTimerRef.current);
      notifyTimerRef.current = null;
      onTicksChangeRef.current?.(localTickOverridesRef.current);
    }
  }, []);

  const handleTickChange = useCallback((blockId, checked) => {
    const masterDefault = masterTicksRef.current.get(blockId) ?? false;
    const next = { ...localTickOverridesRef.current };
    if (checked === masterDefault) delete next[blockId];
    else next[blockId] = checked;
    applyLocalTickOverrides(next);
    pendingEchoRef.current = true;
    notifyHost(next);
  }, [applyLocalTickOverrides, notifyHost, masterTicksRef]);

  const handleTickOverridesChange = useCallback((next) => {
    applyLocalTickOverrides(next);
    pendingEchoRef.current = true;
    clearTimeout(notifyTimerRef.current);
    notifyTimerRef.current = null;
    onTicksChangeRef.current?.(next);
  }, [applyLocalTickOverrides]);

  return { handleTickChange, handleTickOverridesChange };
}
